import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Eye, Pencil, Plus, Trash2 } from "lucide-react";

import { api, ApiError, type AuditEntry, type Customer } from "../../api";
import { Modal } from "../../components/Modal";
import { Select } from "../../components/Select";
import { useSwitchAccount } from "../../hooks/useSwitchAccount";
import { EmptyState, Note, SettingsSection } from "./shared";

const ACTIONS: Record<string, string> = {
  customer_created: "Created customer",
  customer_renamed: "Renamed customer",
  customer_deleted: "Deleted customer",
  person_added: "Added person",
  person_changed: "Changed person",
  person_removed: "Removed person",
  box_claimed: "Claimed box",
  box_placed: "Placed box",
  box_released: "Released box",
  box_assigned_to_customer: "Assigned box to customer",
  store_assigned_to_customer: "Moved store to customer",
  view_as_started: "Started viewing as customer",
  view_as_ended: "Stopped viewing as customer",
};

function detailText(details: AuditEntry["details"]): string {
  if (!details) return "";
  return Object.entries(details)
    .filter(([, v]) => v !== null && v !== "" && v !== undefined)
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(" · ");
}

type Draft = { id?: number; name: string };

/** IntraScene staff: the customer accounts, and who did what in them. */
export function CustomersSection() {
  const qc = useQueryClient();
  const { switchTo, busy } = useSwitchAccount();
  const customers = useQuery({ queryKey: ["customers"], queryFn: api.customers.list });
  const [filter, setFilter] = useState("");
  const audit = useQuery({
    queryKey: ["audit-log", filter],
    queryFn: () => api.customers.auditLog(filter ? Number(filter) : undefined),
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["audit-log"] });
  };
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const save = useMutation({
    mutationFn: (d: Draft) =>
      d.id != null ? api.customers.rename(d.id, d.name.trim()) : api.customers.create(d.name.trim()),
    onSuccess: () => {
      setDraft(null);
      refresh();
    },
    onError: fail("Could not save the customer."),
  });
  const remove = useMutation({
    mutationFn: (c: Customer) => api.customers.delete(c.id),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (e: unknown) => {
      setRemoving(null);
      fail("Could not delete the customer.")(e);
    },
  });

  const all = customers.data ?? [];
  const nameOf = (id: number | null) =>
    id == null ? "—" : (all.find((c) => c.id === id)?.name ?? `#${id}`);
  const inUse = (c: Customer) => c.locations + c.boxes + c.people > 0;

  return (
    <SettingsSection
      title="Customers"
      description="Customer accounts. Each one sees only its own locations, boxes and people."
      hint={
        <>
          A customer's locations come from the boxes it adds; staff can also
          move a store or a stock box into an account. <b>View as</b> shows the
          app exactly as that customer's owners see it, and is recorded below.
        </>
      }
      actions={
        <button className="bf-button" onClick={() => setDraft({ name: "" })}>
          <Plus size={16} className="mr-2" /> New customer
        </button>
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {customers.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading customers…</div>
      ) : all.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No customers yet"
          hint="Everything is staff-only until a customer account exists."
        />
      ) : (
        <div className="bf-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Locations</th>
                <th className="p-3">Boxes</th>
                <th className="p-3">People</th>
                <th className="p-3">Since</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {all.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 tabular-nums">{c.locations}</td>
                  <td className="p-3 tabular-nums">{c.boxes}</td>
                  <td className="p-3 tabular-nums">{c.people}</td>
                  <td className="p-3 text-xs text-bf-muted">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        className="flex items-center gap-1 text-xs text-bf-muted hover:text-bf-text disabled:opacity-40"
                        disabled={busy}
                        onClick={() => switchTo(c.id)}
                      >
                        <Eye size={14} /> View as
                      </button>
                      <button
                        className="text-bf-muted hover:text-bf-text"
                        aria-label={`Rename ${c.name}`}
                        onClick={() => setDraft({ id: c.id, name: c.name })}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="text-bf-muted hover:text-red-400 disabled:opacity-40"
                        aria-label={`Delete ${c.name}`}
                        title={
                          inUse(c) ? "Move its locations, boxes and people out first" : undefined
                        }
                        disabled={inUse(c)}
                        onClick={() => setRemoving(c)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-2 pt-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-white">Account activity</h3>
          <Select
            className="w-56"
            ariaLabel="Filter by customer"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "", label: "All customers" },
              ...all.map((c) => ({ value: String(c.id), label: c.name })),
            ]}
          />
        </div>
        {(audit.data ?? []).length === 0 ? (
          <p className="text-sm text-bf-muted">
            {audit.isLoading ? "Loading…" : "Nothing recorded yet."}
          </p>
        ) : (
          <div className="bf-card max-h-96 overflow-auto p-0">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bf-surface text-left text-xs uppercase tracking-wide text-bf-muted">
                <tr>
                  <th className="p-3">When</th>
                  <th className="p-3">Who</th>
                  <th className="p-3">What</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {(audit.data ?? []).map((a) => (
                  <tr key={a.id} className="border-t border-white/5 align-top">
                    <td className="whitespace-nowrap p-3 text-xs text-bf-muted">
                      {new Date(a.ts).toLocaleString()}
                    </td>
                    <td className="p-3">{a.username}</td>
                    <td className="p-3">{ACTIONS[a.action] ?? a.action}</td>
                    <td className="p-3">{nameOf(a.customer_id)}</td>
                    <td className="p-3 font-mono text-xs text-bf-muted">{detailText(a.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {draft && (
        <Modal onClose={() => setDraft(null)} labelledBy="customer-draft-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(draft);
            }}
          >
            <h3 id="customer-draft-title" className="text-lg font-medium text-white">
              {draft.id != null ? "Rename customer" : "New customer"}
            </h3>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Name</span>
              <input
                className="bf-input"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Acme Retail"
                required
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => setDraft(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button disabled:opacity-40"
                type="submit"
                disabled={save.isPending || !draft.name.trim()}
              >
                {save.isPending ? "Saving…" : draft.id != null ? "Save" : "Create customer"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {removing && (
        <Modal onClose={() => setRemoving(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Delete {removing.name}?</h3>
            <p className="text-sm text-bf-muted">
              The account has no locations, boxes or people. Its activity
              history is kept.
            </p>
            <div className="flex justify-end gap-2">
              <button className="bf-button bg-white/10 bg-none" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={remove.isPending}
                onClick={() => remove.mutate(removing)}
              >
                {remove.isPending ? "Deleting…" : "Delete customer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}
